import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { UserProvider } from './context/UserContext';
import { MatchProvider } from "./context/MatchContext";
import { ContestProvider } from "./context/ContestContext";

// --- EXISTING USER COMPONENTS ---
import Splash from "./components/Splash";
import Home from "./pages/Home";
import ProtectedRoute from "./components/ProtectedRoute";
import Auth from "./pages/Auth";
// import BottomNavigation from "./components/BottomNavigation";
import Contests from "./pages/Contests";
import CreateTeam from "./pages/CreateTeam";
import MyContests from "./pages/MyContests";
import Wallet from "./pages/Wallet";
import HowToPlay from "./pages/HowToPlay";
import PointSystem from "./pages/PointSystem";
import HelpSupport from "./pages/HelpSupport";
import Settings from "./pages/Settings";
import ReferEarn from "./pages/ReferEarn";
import Account from "./pages/Account";
import More from "./pages/More";
import VerifyAccount from "./pages/VerifyAccount";
import PlayingHistory from "./pages/PlayingHistory";
import Profile from "./pages/Profile";
import ChooseCaptain from "./pages/ChooseCaptain";
import TeamPreview from "./pages/TeamPreview";

// --- ADMIN IMPORTS (Lazy Loading for Performance) ---
// Admin Layout aapne pehle se import kiya tha, usse waisa hi rakha hai
import AdminLayout from "./admin/components/AdminLayout";
import AdminProtectedRoute from "./admin/components/AdminProtectedRoute";
import ContestDetail from "./pages/ContestDetail";
import Notifications from "./pages/Notifications";
import PlayerRoles from "./admin/pages/PlayerRoles";
import KYCHistory from "./admin/pages/KYCHistory";
import AdminTickets from "./admin/pages/AdminTickets";
import AdminDeletionRequests from "./admin/pages/AdminDeletionRequests";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import FairPlayPolicy from "./pages/FairPlayPolicy";
import ScrollToTop from "./components/ScrollToTop";
import ManageBanners from "./admin/pages/ManageBanners";
import CreateTemplate from "./admin/pages/CreateTemplate";
import MatchSettlements from "./admin/pages/MatchSettlements";
import MatchDashboard from "./pages/MatchDashboard";
import LiveLeaderboard from "./pages/LiveLeaderboard";

// Baaki Admin Pages ko lazy load kar rahe hain
const AdminLogin = lazy(() => import("./admin/pages/AdminLogin"));
const Dashboard = lazy(() => import("./admin/pages/Dashboard"));
const UserList = lazy(() => import("./admin/pages/UserList"));
const KYCRequests = lazy(() => import("./admin/pages/KYCRequests"));
const AdminSettings = lazy(() => import("./admin/pages/Settings")); // Renamed to avoid clash with User Settings

// --- HELPER COMPONENT: USER LAYOUT ---
// Iska kaam hai ki Bottom Navigation sirf User Pages par dikhaye
const UserLayout = () => {
  return (
    <>
      <div className="pb-16"> {/* Add padding bottom taki content nav ke piche na chupe */}
        <Outlet />
      </div>
      {/* <BottomNavigation /> */}
    </>
  );
};

function App() {

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, []);



  if (loading) return <Splash />;

  const isAuthenticated = () => {
    return !!localStorage.getItem("token");
  };

  return (
    <UserProvider>
      <MatchProvider>
        <ContestProvider>
          <BrowserRouter>
          <ScrollToTop />
            {/* Suspense is required for Lazy Loading Admin Pages */}
            <Suspense fallback={<div className="d-flex justify-content-center mt-5">Loading...</div>}>
              <Routes>

                {/* =========================================
              1. ADMIN ROUTES (No Bottom Navigation)
          ========================================= */}
                <Route path="/win11-control-room/login" element={<AdminLogin />} />

                {/* 2. PROTECTED ADMIN ROUTES */}
                <Route element={<AdminProtectedRoute />}>
                  <Route
                    path="/admin/match-settlements"
                    element={<Navigate to="/win11-control-room/match-settlements" replace />}
                  />

                  {/* Yahan 'path="/admin/panel"' ki jagah wapas 'path="/admin"' karo */}

                  <Route path="/win11-control-room" element={<AdminLayout />}>

                    <Route index element={<Dashboard />} />

                    {/* Note: Yahan "/" mat lagana shuru mein, ye relative path hain */}

                    <Route path="player-roles" element={<PlayerRoles/>} />
                    <Route path="kyc-history" element={<KYCHistory/>} />
                    <Route path="create-template" element={<CreateTemplate/>} />
                    <Route path="match-settlements" element={<MatchSettlements/>} />
                    <Route path="ticket-support" element={<AdminTickets/>} />
                    <Route path="account-deletion-requests" element={<AdminDeletionRequests/>} />
                    <Route path="manage-banners" element={<ManageBanners/>} />
                  
                    <Route path="users" element={<UserList />} />
                    <Route path="kyc" element={<KYCRequests />} />
                    <Route path="settings" element={<AdminSettings />} />
                    

                  </Route>
                </Route>


                {/* =========================================
              2. USER ROUTES (With Bottom Navigation)
          ========================================= */}

                <Route element={<UserLayout />}>

                  <Route path="/" element={<Navigate to="/home" />} />
                  <Route path="/login" element={<Auth />} />

                  <Route
                    path="/home"
                    element={
                      <ProtectedRoute>
                        <Home />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/contests/:matchId" element={<ProtectedRoute><Contests /></ProtectedRoute>} />
                  <Route path="/contest-detail/:contestId" element={<ProtectedRoute><ContestDetail /></ProtectedRoute>} />
                  <Route path="/create-team" element={<ProtectedRoute><CreateTeam /></ProtectedRoute>} />
                  <Route path="/choose-captain" element={<ProtectedRoute><ChooseCaptain /></ProtectedRoute>} />
                  <Route path="/team-preview" element={<ProtectedRoute><TeamPreview /></ProtectedRoute>} />
                  <Route path="/team-preview/:teamId" element={<ProtectedRoute><TeamPreview /></ProtectedRoute>} />
                  <Route path="/my-contests" element={<ProtectedRoute><MyContests /></ProtectedRoute>} />
                  <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                  <Route path="/verify-account" element={<ProtectedRoute><VerifyAccount /></ProtectedRoute>} />
                  <Route path="/playing-history" element={<ProtectedRoute><PlayingHistory /></ProtectedRoute>} />
                  <Route path="/more" element={<ProtectedRoute><More /></ProtectedRoute>} />
                  <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
                  <Route path="/refer-earn" element={<ProtectedRoute><ReferEarn /></ProtectedRoute>} />
                  <Route path="/how-to-play" element={<ProtectedRoute><HowToPlay /></ProtectedRoute>} />
                  <Route path="/point-system" element={<ProtectedRoute><PointSystem /></ProtectedRoute>} />
                  <Route path="/help-support" element={<ProtectedRoute><HelpSupport /></ProtectedRoute>} />
                  <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                  <Route path="/match-dashboard/:matchId" element={<MatchDashboard />} />
                  <Route path="/live-leaderboard/:contestId" element={<LiveLeaderboard />} />

                  {/* User Settings */}

                  <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                  <Route path="/terms-and-conditions" element={<ProtectedRoute><TermsAndConditions /></ProtectedRoute>} />
                  <Route path="/privacy-policy" element={<ProtectedRoute><PrivacyPolicy /></ProtectedRoute>} />
                  <Route path="/fair-play-policy" element={<ProtectedRoute><FairPlayPolicy /></ProtectedRoute>} />

                </Route>

                {/* 👇 MAGIC CODE: HANDLE WRONG URLs 👇 */}
                {/* Isko sabse LAST mein rakhna zaroori hai */}

                <Route path="*" element={
                  isAuthenticated()
                    ? <Navigate to="/home" replace />  // Agar Login hai -> Home bhejo
                    : <Navigate to="/login" replace /> // Agar Login nahi hai -> Login/Auth bhejo
                } />

              </Routes>

            </Suspense>
          </BrowserRouter>
        </ContestProvider>
      </MatchProvider>
    </UserProvider>
  );
}

export default App;
